import {
  addWeeks,
  differenceInDays,
  differenceInMinutes,
  eachDayOfInterval,
  endOfWeek,
  format,
  isToday,
  isTomorrow,
  isYesterday,
  Locale,
  parseISO,
  startOfWeek,
  subWeeks,
} from 'date-fns'
import { sv, enGB, nb } from 'date-fns/locale'
import { fromZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { SupportedLanguage } from '@estercare/ester-shared'
import { HealthCallAppointment } from '../api/ester-b2b-api/private/healthCalls/types'

export const formatDisplayStartTime = (appointment: HealthCallAppointment, language: string, t: TFunction): string => {
  const { date, startTime, endTime } = appointment
  const locale = getDateFnsLocale(language)
  const incomingStartTime: Date = applyTimeZonetoWebdocDate(date, startTime)
  const incomingEndTime: Date = applyTimeZonetoWebdocDate(date, endTime)
  const displayStartTime = getHourAndMinuteFromDate(incomingStartTime)
  const displayEndTime = getHourAndMinuteFromDate(incomingEndTime)
  if (isToday(incomingStartTime)) {
    return `${t('employee.healthCall.todayAt')} ${displayStartTime}-${displayEndTime}`
  }
  if (isTomorrow(incomingStartTime)) {
    return `${t('employee.healthCall.tomorrowAt')} ${displayStartTime}-${displayEndTime}`
  }
  return `${format(incomingStartTime, 'd MMMM', { locale })} ${t('employee.healthCall.time')} ${displayStartTime}-${displayEndTime}`
}

export const applyTimeZonetoWebdocDate = (date: string, time: string): Date => {
  return fromZonedTime(parseISO(`${date}T${time}`), 'Europe/Stockholm')
}

export const getHourAndMinuteFromDate = (date: Date) => {
  return format(date, 'HH:mm', { locale: sv })
}

export const getDateFnsLocale = (language: string): Locale => {
  switch (language) {
    case SupportedLanguage.ENGLISH:
      return enGB
    case SupportedLanguage.NORWEGIAN:
      return nb
    case SupportedLanguage.SWEDISH:
    default:
      return sv
  }
}

/**
 * Get the start of the week (Monday) for a given date
 */
export const getWeekStart = (date: Date): Date => {
  return startOfWeek(date, { weekStartsOn: 1 }) // 1 = Monday
}

/**
 * Get the end of the week (Sunday) for a given date
 */
export const getWeekEnd = (date: Date): Date => {
  return endOfWeek(date, { weekStartsOn: 1 })
}

/**
 * Get the previous week's start date
 */
export const getPreviousWeek = (currentWeekStart: Date): Date => {
  return subWeeks(currentWeekStart, 1)
}

/**
 * Get the next week's start date
 */
export const getNextWeek = (currentWeekStart: Date): Date => {
  return addWeeks(currentWeekStart, 1)
}

/**
 * Format day name abbreviation for display
 * @param date - The date to format
 * @param locale - The locale to use (sv, en, or nb)
 * @returns Formatted string like "Mon", "Mån", or "Man" (Norwegian)
 */
export const formatDayName = (date: Date, locale: SupportedLanguage = SupportedLanguage.SWEDISH): string => {
  const localeObj = getDateFnsLocale(locale)
  return format(date, 'EEE', { locale: localeObj })
}

/**
 * Format day number for display
 * @param date - The date to format
 * @returns Formatted string like "22" or "23"
 */
export const formatDayNumber = (date: Date): string => {
  return format(date, 'd')
}

/**
 * Format a date for display in the month/year header
 * @param date - The date to format
 * @param locale - The locale to use (sv, en, or nb)
 * @returns Formatted string like "January 2026", "Januari 2026", or "Januar 2026" (Norwegian)
 */
export const formatMonthYear = (date: Date, locale: SupportedLanguage = SupportedLanguage.SWEDISH): string => {
  const localeObj = getDateFnsLocale(locale)
  return format(date, 'MMMM yyyy', { locale: localeObj })
}

/**
 * Format a time for display in time slots
 * @param date - The date to format
 * @returns Formatted string like "09:00"
 */
export const formatTime = (date: Date): string => {
  return format(date, 'HH:mm')
}

/**
 * Format a date to ISO string for API calls
 * @param date - The date to format
 * @returns ISO date string like "2026-01-20"
 */
export const formatDateForAPI = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

/**
 * Get all days in the week
 */
export const getDaysInWeek = (weekStart: Date): Date[] => {
  const weekEnd = getWeekEnd(weekStart)
  return eachDayOfInterval({ start: weekStart, end: weekEnd })
}

export const formatRelativeTime = (date: Date, t: TFunction) => {
  try {
    const now = new Date()
    const minutesAgo = differenceInMinutes(now, date)

    if (minutesAgo < 1) {
      return t('employee.messages.relativeTimeJustNow')
    }

    if (minutesAgo < 60) {
      return `${minutesAgo} ${t('employee.messages.relativeTimeMinutesAgo')}`
    }

    if (isToday(date)) {
      const hoursAgo = Math.floor(minutesAgo / 60)
      return `${hoursAgo} ${t('employee.messages.relativeTimeHoursAgo')}`
    }

    if (isYesterday(date)) {
      return `${t('employee.messages.relativeTimeYesterday')}, ${format(date, 'HH:mm')}`
    }

    const daysAgo = differenceInDays(now, date)

    if (daysAgo <= 7) {
      return `${daysAgo} ${t('employee.messages.relativeTimeDaysAgo')}`
    }

    return format(date, 'd MMM yyyy', { locale: sv })
  } catch {
    return date.toString()
  }
}
